import { Button, Flex, styled } from '@driftt/dds'
import React, { useEffect, useRef } from 'react'

const LINE_HEIGHT = 16
const MAX_LINES = 7
const MAX_HEIGHT = LINE_HEIGHT * (MAX_LINES + 2) // We add 2 to consider padding

const Container = styled(Flex, {
  position: 'relative',
  width: '100%',
  height: '100%',
})

const TextArea = styled('textarea', {
  fontFamily: '$primary',
  width: '100%',
  maxHeight: `${MAX_HEIGHT}px`,
  resize: 'none',
  boxSizing: 'border-box',
  padding: '$2 72px $2 $2',
  overflow: 'hidden',
  borderRadius: '4px',
  border: '1px solid $blue3',
  '&active': {
    border: '1px solid $blue5',
  },
  '&focus': {
    border: '1px solid $blue6',
  },
})

const ButtonContainer = styled(Flex, {
  position: 'absolute',
  top: '0',
  right: '0',
  bottom: '0',
  padding: '10px',
})

const TextareaWithButton = ({
  buttonLabel,
  placeholder = '',
  value,
  disabled,
  loading,
  onChange,
  onClickButton,
}: {
  buttonLabel: string
  placeholder?: string
  value: string
  disabled?: boolean
  loading?: boolean
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onClickButton: () => void
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    const resizeTextarea = () => {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }

    textarea.addEventListener('input', resizeTextarea)
    resizeTextarea()

    return () => {
      textarea.removeEventListener('input', resizeTextarea)
    }
  }, [disabled]) // tracking disabled here, allows to reset size when sent

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onClickButton()
    }
    // If Shift+Enter is pressed, allow the default behavior (new line)
  }

  return (
    <Container direction="row" alignItems="center" justifyContent="center">
      <TextArea
        ref={textareaRef}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
        rows={1}
        onKeyDown={handleKeyDown}
      />
      <ButtonContainer
        direction="row"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          variant="secondary"
          disabled={!value || disabled}
          loading={loading}
          onClick={onClickButton}
        >
          {buttonLabel}
        </Button>
      </ButtonContainer>
    </Container>
  )
}

export default TextareaWithButton
